import colors from '#src/client/common/colors.js';
import sizes from '#src/client/common/interactive/sizes.js';
import isEmpty from '#src/client/common/utils/isEmpty.js';
import { splitProps } from 'solid-js';
import style from '#src/client/common/interactive/InputField.module.css';

function Options(props) {
    // In order for the choices to be reactive, we have to ask for them each time, not just assign them within the loop.
    const getTitle = (option) => {
        return String(option.title || option.value);
    };

    return (
        <For each={props.options}>
            {(option) => {
                return (
                    <option
                        selected={props.state === option.value}
                        value={option.value}
                        label={getTitle(option)}>{getTitle(option)}</option>
                );
            }}
        </For>
    );
}

function InputField(props) {
    const [local, others] = splitProps(props, [
        'state',
        'update',

        'inputType',

        'size',
        'color',
        'class',
        'autoWidth',

        // Props for select elements
        'empty',
        'isEmptyDisabled',
        'options',
        'usesOptGroups',

        'onChange',
        'onInput',
    ]);

    const getSize = () => {
        const size = !isEmpty(local.size) ? local.size : sizes.default.name;
        return sizes[size] ? sizes[size].size : size;
    };

    const getColor = () => {
        const color = !isEmpty(local.color) ? local.color : colors.primary;
        return colors[color] ? style[color] : color;
    };

    const classes = () => {
        return [
            local.class ?? '',
            local.autoWidth ? style.autoWidth : '',
            style.input,
            getColor(),
        ].join(' ');
    };

    const onInput = (e) => {
        if (local.onInput) {
            const shouldContinue = local.onInput(e);
            if (shouldContinue === false) {
                return;
            }
        }

        local.update(e.currentTarget.value);
    };

    const onChange = (e) => {
        if (local.onChange) {
            const shouldContinue = local.onChange(e);
            if (shouldContinue === false) {
                return;
            }
        }

        if (local.inputType === 'select') {
            const index = e.currentTarget.selectedIndex;

            if (index <= 0) {
                local.update(null);
                return;
            }

            let options = local.options;
            if (local.usesOptGroups) {
                options = local.options.flatMap((optGroup) => {
                    return optGroup.choices;
                });
            }

            // When selecting empty, return null.
            const out = index <= 0 ? null : options[index - 1].value;
            local.update(out);
        }
    };

    return (
        <Switch>
            <Match when={local.inputType === 'input'}>
                <input
                    tabIndex="0"
                    type="text"
                    {...others}
                    style={{ '--size': getSize() }}
                    class={classes()}
                    value={local.state}
                    onInput={onInput} />
            </Match>
            <Match when={local.inputType === 'textarea'}>
                <textarea
                    tabIndex="0"
                    rows="3"
                    {...others}
                    style={{ '--size': getSize() }}
                    class={classes()}
                    value={local.state}
                    onInput={onInput} />
            </Match>
            <Match when={local.inputType === 'select'}>
                <select
                    tabindex="0"
                    {...others}
                    style={{ '--size': getSize() }}
                    class={classes()}
                    onChange={onChange}>

                    <option value={''} label={local.empty ?? '-- choose --'} disabled={local.isEmptyDisabled} hidden={local.isEmptyDisabled}>{local.empty ?? '-- choose --'}</option>

                    <Switch>
                        <Match when={local.usesOptGroups}>
                            <For each={local.options}>
                                {(optGroup) => (
                                    <optgroup label={optGroup.groupTitle}>
                                        <Options
                                            state={local.state}
                                            options={optGroup.choices} />
                                    </optgroup>
                                )}
                            </For>
                        </Match>
                        <Match when={!local.usesOptGroups}>
                            <Options
                                state={local.state}
                                options={local.options} />
                        </Match>
                    </Switch>
                </select>
            </Match>
        </Switch>
    );
}

export default InputField;
